import { overtimeConstants } from './OvertimeConstants';

// ============================================
// Utility Functions for Normalization
// ============================================

/**
 * Creates a composite ID for shift signups
 */
const createSignupId = (employeeId, shiftName) => `${employeeId}-${shiftName}`;

/**
 * Normalizes an array of employees from API response
 * into normalized state structure
 */
export const normalizeEmployees = (employeesArray) => {
    const employees = {
        byId: {},
        allIds: []
    };
    
    const shiftSignups = {
        byId: {},
        byEmployeeId: {}
    };

    employeesArray.forEach((employee, index) => {
        const { otShiftSignUps, ...employeeData } = employee;
        
        // Store employee without nested signups
        // First employee expanded, rest collapsed
        employees.byId[employee.aaId] = {
            ...employeeData,
            collapsed: index !== 0
        };
        employees.allIds.push(employee.aaId);
        
        // Normalize shift signups
        shiftSignups.byEmployeeId[employee.aaId] = [];
        
        if (otShiftSignUps) {
            const shiftDate = employeeData.otDayShifts?.[0]?.shiftDate || '';
            otShiftSignUps.forEach((signup) => {
                const signupId = createSignupId(employee.aaId, signup.shiftName);
                shiftSignups.byId[signupId] = {
                    ...signup,
                    id: signupId,
                    employeeId: employee.aaId,
                    schedBidGroup: employee.schedBidGroup || '',
                    otGroup: employee.otGroup || '',
                    shiftDate,
                };
                shiftSignups.byEmployeeId[employee.aaId].push(signupId);
            });
        }
    });

    return { employees, shiftSignups };
};

// ============================================
// Initial State (Normalized)
// ============================================
const initialState = {
    employees: { byId: {}, allIds: [] },
    shiftSignups: { byId: {}, byEmployeeId: {} },
    overtimeSlots: [],
    showLoader: false,
    fetchSuccess: null,
    fetchError: null,
    otSkillsSuccess: null,
    otSkillsData: null,
    callSheetData: null,
};

// ============================================
// Reducer
// ============================================
export const OvertimeReducer = (state = initialState, action) => {
    switch (action.type) {
        case overtimeConstants.SHOW_OVERTIME_LOADER: {
            return {
                ...state,
                showLoader: action.data
            };
        }

        case overtimeConstants.GET_OVERTIME_DATA_SUCCESS: {
            // Normalize incoming data from API
            const callSheetList = Array.isArray(action.data?.callSheetList) 
                ? action.data.callSheetList 
                : [];
            const { employees, shiftSignups } = normalizeEmployees(callSheetList);
            const callSheetData = {
                callSheetId: action.data?.votId || '',
                callSheetWorkingShift: action.data?.callSheetWorkingShift
            };
            return {
                ...state,
                employees,
                shiftSignups,
                fetchSuccess: true,
                fetchError: null,
                callSheetData
            };
        }

        case overtimeConstants.GET_OVERTIME_DATA_FAILED: {
            return {
                ...state,
                fetchSuccess: false,
                fetchError: action.data
            };
        }

        case overtimeConstants.TOGGLE_ITEM_COLLAPSED: {
            const employee = state.employees.byId[action.employeeId];
            if (!employee) return state;
            
            const willExpand = employee.collapsed;
            
            // If expanding, collapse all others (accordion behavior)
            const updatedById = {};
            for (const id of state.employees.allIds) {
                const emp = state.employees.byId[id];
                if (id === action.employeeId) {
                    updatedById[id] = { ...emp, collapsed: !emp.collapsed };
                } else if (willExpand) {
                    // Collapse others when expanding this one
                    updatedById[id] = { ...emp, collapsed: true };
                } else {
                    updatedById[id] = emp;
                }
            }
            
            return {
                ...state,
                employees: {
                    ...state.employees,
                    byId: updatedById
                }
            };
        }

        case overtimeConstants.SET_CONTACT: {
            const employee = state.employees.byId[action.employeeId];
            if (!employee) return state;
            
            return {
                ...state,
                employees: {
                    ...state.employees,
                    byId: {
                        ...state.employees.byId,
                        [action.employeeId]: {
                            ...employee,
                            contact: {
                                ...employee.contact,
                                ...action.contact
                            }
                        }
                    }
                }
            };
        }

        case overtimeConstants.SELECT_DECISION: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;
            
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            pendingDecision: action.decision
                        }
                    }
                }
            };
        }

        case overtimeConstants.CONFIRM_DECISION_SUCCESS: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            const decision = action.decision;
            if (!signup) return state;
            
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            status: decision,
                            lastTimeStamp: action.lastTimeStamp || '',
                            savedAt: new Date().toISOString()
                        }
                    }
                }
            };
        }

        case overtimeConstants.SET_OVERTIME_SLOTS: {
            return {
                ...state,
                overtimeSlots: action.data
            };
        }

        case overtimeConstants.RESOLVE_OT_WB_FAILED_SUCCESS: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            status: action.resolveAction === 'NOACTION-NEEDED' ? 'REFUSED' : 'ACCEPT',
                            error: null,
                            resolvedAt: new Date().toISOString()
                        }
                    }
                }
            };
        }

        case overtimeConstants.CONFIRM_DECISION_FAILED:
        case overtimeConstants.ADD_OT_SHIFT_FAILED:
        case overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            
            return {
                ...state,
                shiftSignups: signup ? {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            error: action.data,
                            confirmed: false
                        }
                    }
                } : state.shiftSignups,
                fetchError: action.data
            };
        }

        case overtimeConstants.CLEAR_SIGNUP_ERROR: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;
            
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            error: null
                        }
                    }
                }
            };
        }

        case overtimeConstants.OVERTIME_CLEANUP: {
            return {
                ...state,
                employees: { byId: {}, allIds: [] },
                shiftSignups: { byId: {}, byEmployeeId: {} },
                overtimeSlots: [],
                fetchSuccess: null,
                fetchError: null,
                callSheetData: null,
                otSkillsSuccess: null,
                otSkillsData: null
            };
        }

        case overtimeConstants.GET_OT_SKILLS_DATA: {
            return {
                ...state,
                otSkillsData: action.payload
            };
        }

        case overtimeConstants.GET_OT_SKILLS_SUCCESS: {
            return {
                ...state,
                otSkillsSuccess: action.payload
            };
        }

        default:
            return state;
    }
};

export default OvertimeReducer;
